import React from 'react';
import { Sparkles, Loader2, Copy, CheckCircle2, ArrowRight, User, ListChecks, FileText, Mail, Bell } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ai } from '@/lib/api';

const TOOLS = [
  { id: 'qualocc', icon: User, label: 'Qualification → Occupation', body: 'Suggests ANZSCO + assessing body from qualifications and experience.' },
  { id: 'rfi', icon: FileText, label: 'RFI / s56 Drafter', body: 'Drafts a formal response from matter context and evidence.' },
  { id: 'pathway', icon: ListChecks, label: 'Visa Pathway Recommender', body: 'Ranks top-3 subclasses (coming next).' },
  { id: 'triage', icon: Mail, label: 'Email Triage', body: 'Classifies inbound email and drafts a reply (coming next).' },
  { id: 'pulse', icon: Bell, label: 'Policy Impact Analyser', body: 'Lists open matters impacted by a policy update (coming next).' },
];

export default function AICopilotPage() {
  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#2ECC9A]/10">
            <Sparkles className="h-5 w-5 text-[#2ECC9A]" />
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">AI Copilot</h1>
            <div className="text-sm text-slate-500">Live · grounded in DHA + OMARA + assessing-body corpus · human-review required</div>
          </div>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Live — Emergent Universal LLM</Badge>
      </div>

      <Tabs defaultValue="qualocc" className="mt-6">
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <TabsList className="flex h-auto flex-col items-stretch gap-1 bg-transparent p-0">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.id} value={t.id}
                  className="group justify-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-left data-[state=active]:border-[#2ECC9A] data-[state=active]:bg-[#2ECC9A]/5 data-[state=active]:shadow-none">
                  <Icon className="h-4 w-4 shrink-0 text-slate-500 group-data-[state=active]:text-[#2ECC9A]" />
                  <div>
                    <div className="text-sm font-medium text-[#1A3A5C]">{t.label}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{t.body}</div>
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="qualocc" className="mt-0"><QualOccTool /></TabsContent>
          <TabsContent value="rfi" className="mt-0"><RfiTool /></TabsContent>
          <TabsContent value="pathway" className="mt-0"><ComingSoon title="Visa Pathway Recommender" /></TabsContent>
          <TabsContent value="triage" className="mt-0"><ComingSoon title="Email Triage" /></TabsContent>
          <TabsContent value="pulse" className="mt-0"><ComingSoon title="Policy Impact Analyser" /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function QualOccTool() {
  const [f, setF] = React.useState({ qualification: 'Bachelor of Engineering (Computer Science)', experience_years: 6, role_title: 'Senior Software Engineer', english_proficiency: 'PTE 79+ (Proficient)' });
  const [out, setOut] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  const run = async (e) => {
    e.preventDefault(); setErr(''); setOut(null); setLoading(true);
    try {
      const data = await ai.qualOcc({ ...f, experience_years: Number(f.experience_years) });
      setOut(data);
    } catch (er) { setErr(er?.response?.data?.detail || 'AI call failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <div className="text-sm font-semibold text-[#1A3A5C]">Suggest occupation &amp; assessing body</div>
        <div className="mt-1 text-xs text-slate-500">Live LLM — maps qualifications to ANZSCO and the correct Australian assessing authority.</div>
        <form onSubmit={run} className="mt-4 grid gap-4 md:grid-cols-2">
          <div><Label>Highest qualification</Label><Input value={f.qualification} onChange={(e)=>setF({...f, qualification: e.target.value})} className="mt-1" /></div>
          <div><Label>Years of experience</Label><Input type="number" min={0} step={0.5} value={f.experience_years} onChange={(e)=>setF({...f, experience_years: e.target.value})} className="mt-1" /></div>
          <div><Label>Most recent role title</Label><Input value={f.role_title} onChange={(e)=>setF({...f, role_title: e.target.value})} className="mt-1" /></div>
          <div><Label>English proficiency</Label><Input value={f.english_proficiency} onChange={(e)=>setF({...f, english_proficiency: e.target.value})} className="mt-1" /></div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={loading} className="h-10 gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Suggest
            </Button>
          </div>
        </form>

        {err && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{err}</div>}

        {out && (
          <>
            <div className="mt-6 rounded-lg border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-[#2ECC9A]">Suggested occupation</div>
                <Badge className="bg-[#2ECC9A]/10 text-[#2ECC9A]">{out.confidence}% confidence</Badge>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div>
                  <div className="font-semibold text-[#1A3A5C]">{out.occupation}</div>
                  <div className="text-xs text-slate-500">ANZSCO {out.anzsco} · {out.list}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
                <div className="rounded-md bg-[#1A3A5C] px-3 py-1.5 text-sm font-medium text-[#2ECC9A]">{out.assessing_body_full || out.assessing_body}</div>
              </div>
              <div className="mt-3 text-xs text-slate-700">Reasoning: {out.reasoning}</div>
            </div>

            {out.evidence_checklist && (
              <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-medium text-[#1A3A5C]">Auto-loaded {out.assessing_body} evidence list</div>
                <ul className="mt-2 space-y-1 text-xs text-slate-700">
                  {out.evidence_checklist.map((i, idx) => (
                    <li key={idx} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3 w-3 text-emerald-600" /> {i}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RfiTool() {
  const [f, setF] = React.useState({ applicant_name: 'John Smith', matter_ref: 'NM-2025-0142', subclass: '186', section: 's56 — request for further information', request_summary: 'Updated employment reference with ANZSCO-aligned duties; 12 months of payslips.' });
  const [out, setOut] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const run = async (e) => {
    e.preventDefault(); setErr(''); setOut(''); setLoading(true);
    try {
      const data = await ai.rfiDraft(f);
      setOut(data.text);
    } catch (er) { setErr(er?.response?.data?.detail || 'AI call failed'); }
    finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard.writeText(out); setCopied(true); setTimeout(()=>setCopied(false), 2000); };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <div className="text-sm font-semibold text-[#1A3A5C]">Draft RFI response</div>
        <form onSubmit={run} className="mt-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div><Label>Applicant</Label><Input value={f.applicant_name} onChange={(e)=>setF({...f, applicant_name: e.target.value})} className="mt-1" /></div>
            <div><Label>Matter ref</Label><Input value={f.matter_ref} onChange={(e)=>setF({...f, matter_ref: e.target.value})} className="mt-1" /></div>
            <div><Label>Subclass</Label><Input value={f.subclass} onChange={(e)=>setF({...f, subclass: e.target.value})} className="mt-1" /></div>
          </div>
          <div><Label>Section</Label><Input value={f.section} onChange={(e)=>setF({...f, section: e.target.value})} className="mt-1" /></div>
          <div><Label>What did DHA ask for?</Label><Textarea rows={3} value={f.request_summary} onChange={(e)=>setF({...f, request_summary: e.target.value})} className="mt-1" /></div>
          <Button type="submit" disabled={loading} className="h-10 gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft response
          </Button>
        </form>

        {err && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{err}</div>}

        {out && (
          <div className="mt-6 rounded-lg border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-[#2ECC9A]">Drafted response — review before use</div>
              <Button variant="outline" onClick={copy} className="h-8 gap-1.5 border-[#2ECC9A]/30">
                {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}{copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-700">{out}</pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ComingSoon({ title }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="flex items-center justify-center p-16 text-center">
        <div>
          <Sparkles className="mx-auto h-8 w-8 text-[#2ECC9A]" />
          <div className="mt-3 font-semibold text-[#1A3A5C]">{title}</div>
          <div className="mt-1 text-xs text-slate-500">Powered by the same Emergent LLM — shipping next.</div>
        </div>
      </CardContent>
    </Card>
  );
}
